import { ChangeEvent, useState } from 'react';
import { useFindAdvisorsMutation } from '../../../../../../api/useFindAdvisorsMutation';
import { CampaignAccessDTO } from '../../../../../../api/useCampaignAccessQuery';
import { SAVE_BUTTON_TEXT } from '../../../../CampaignSettings.meta';

interface Message {
  message: string;
  type?: 'error' | 'success';
}

interface UseSearchAdvisorsParams {
  campaignAccess: CampaignAccessDTO;
  setCampaignAccess(campaignAccess: CampaignAccessDTO): void;
}

export const useSearchAdvisors = ({
  campaignAccess,
  setCampaignAccess,
}: UseSearchAdvisorsParams) => {
  const [inputValue, setInputValue] = useState<string>('');

  const [message, setMessage] = useState<Message | null>(null);

  const { mutateAsync: findAdvisors, isLoading: isFindingAdvisors } = useFindAdvisorsMutation();

  const onSearchTermChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setInputValue(evt.target.value);
  };

  const onFindAdvisors = async () => {
    setMessage(null);

    try {
      const foundAdvisors = await findAdvisors({ email: inputValue });

      const advisorsFoundWithoutAccess = foundAdvisors.filter(
        advisorFound =>
          !campaignAccess.relationships.some(
            advisorWithAccess => advisorWithAccess.adviserId === advisorFound.AdviserId,
          ),
      );

      if (advisorsFoundWithoutAccess.length === 0) {
        setMessage({
          message: 'Advisor already with access.',
        });
      } else {
        setMessage({
          type: 'success',
          message: `Please click "${SAVE_BUTTON_TEXT}" to confirm.`,
        });

        setCampaignAccess({
          ...campaignAccess,
          relationships: [
            ...campaignAccess.relationships,
            ...advisorsFoundWithoutAccess.map(({ AdviserId, LastName, FirstName, IsManager }) => ({
              adviserId: AdviserId,
              lastName: LastName,
              firstName: FirstName,
              isManager: IsManager,
              isRemovable: true,
              isOwner: false,
            })),
          ],
        });
      }
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        setMessage({
          type: 'error',
          message: err.message,
        });
      }
    }
  };

  return {
    isFindingAdvisors,
    onSearchTermChange,
    onFindAdvisors,
    message,
  };
};
